import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Landmark } from "lucide-react";
import { getPeople } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

function PersonCard({ p, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(i, 12) * 0.025, duration: 0.3 }}
    >
      <Link
        to={`/people/${p.id}`}
        className="group flex h-full items-start gap-4 rounded-xl border border-border bg-white p-4 transition-all hover:-translate-y-1 hover:border-navy/30 hover:shadow-xl"
        data-testid={`person-card-${p.id}`}
      >
        {p.wiki_thumb ? (
          <img src={p.wiki_thumb} alt={p.name} loading="lazy" className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-parchment" />
        ) : (
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-navy font-display text-lg font-bold text-white">{initials(p.name)}</span>
        )}
        <div className="min-w-0">
          <div className="font-display text-base font-bold leading-tight text-navy group-hover:text-oldglory-red">{p.name}</div>
          <p className="mt-1 line-clamp-2 text-xs text-ink/70">{p.wiki_desc || p.summary || p.category}</p>
        </div>
      </Link>
    </motion.div>
  );
}

export default function People() {
  const [people, setPeople] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("All");
  const [state, setState] = useState("All");
  const [era, setEra] = useState("All");

  useEffect(() => {
    getPeople(true).then((d) => { setPeople(d.people); setCategories(d.categories); }).catch(() => {});
  }, []);

  const states = useMemo(() => Array.from(new Set(people.map((p) => p.home_state_name).filter(Boolean))).sort(), [people]);
  const eras = useMemo(() => {
    const order = ["Founding Era (–1775)", "19th Century (1776–1865)", "Industrial & Progressive (1866–1915)", "Modern (1916–1965)", "Contemporary (1966–present)"];
    const present = new Set(people.map((p) => p.era).filter(Boolean));
    return order.filter((e) => present.has(e));
  }, [people]);

  const filtered = useMemo(() => people.filter((p) =>
    (cat === "All" || p.category === cat) &&
    (state === "All" || p.home_state_name === state) &&
    (era === "All" || p.era === era)
  ), [people, cat, state, era]);

  const grouped = useMemo(() => {
    const shown = cat === "All" ? categories : [cat];
    return shown.map((c) => ({ cat: c, items: filtered.filter((p) => p.category === c) })).filter((g) => g.items.length);
  }, [filtered, categories, cat]);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12" data-testid="people-page">
      <Seo
        title="100 Notable Americans"
        path="/people"
        description="Explore 100 Notable Americans on USAPulse — founders, presidents, activists, scientists, artists and athletes who shaped the nation, each with an independent profile and Wikipedia reference."
      />
      <SectionHeading kicker="People Pulse · The Heart of America" title="100 Notable Americans" />
      <p className="-mt-4 mb-6 max-w-2xl text-muted-foreground">
        <T>The people who shaped America — founders, activists, scientists, artists and athletes. Each profile is independently written and neutrally covers achievements, context and controversies, with a Wikipedia reference.</T>
      </p>
      <Link to="/presidents" className="mb-6 inline-flex items-center gap-2 rounded-full border border-navy/20 bg-parchment px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white" data-testid="presidents-directory-link">
        <Landmark className="h-4 w-4" /> <T>Browse all U.S. Presidents</T> →
      </Link>

      {/* Filters on top: profession + home state + historical era */}
      <div className="sticky top-11 z-20 -mx-4 mb-8 space-y-3 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="flex flex-wrap gap-2">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${cat === c ? "bg-navy text-white" : "bg-parchment text-navy hover:bg-navy/10"}`}
              data-testid={`people-cat-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              <T>{c === "All" ? "All Professions" : c}</T>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select value={state} onChange={(e) => setState(e.target.value)} className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-navy outline-none focus:border-navy" data-testid="people-filter-state">
            <option value="All">All Home States</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={era} onChange={(e) => setEra(e.target.value)} className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-navy outline-none focus:border-navy" data-testid="people-filter-era">
            <option value="All">All Eras</option>
            {eras.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          {(state !== "All" || era !== "All" || cat !== "All") && (
            <button onClick={() => { setCat("All"); setState("All"); setEra("All"); }} className="text-xs font-semibold text-oldglory-red hover:underline" data-testid="people-filter-clear">
              <T>Clear filters</T>
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{filtered.length} <T>people</T></span>
        </div>
      </div>

      <div className="space-y-12">
        {grouped.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground" data-testid="people-empty"><T>No one matches these filters yet.</T></p>
        ) : grouped.map((g) => (
          <section key={g.cat} data-testid={`people-section-${g.cat.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="mb-4 flex items-center gap-3">
              <h2 className="font-display text-2xl font-black text-navy"><T>{g.cat}</T></h2>
              <span className="rounded-full bg-oldglory-red/10 px-2.5 py-0.5 text-xs font-bold text-oldglory-red">{g.items.length}</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {g.items.map((p, i) => <PersonCard key={p.id} p={p} i={i} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
